/**
 * Formats a phone number into (xxx) xxx-xxxx format.
 * If the input is invalid, it returns the original input.
 * @param phoneNumber - The phone number string to format.
 * @returns Formatted phone number.
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
	if (!phoneNumber) return "";

	// Remove non-numeric characters
	const cleaned = phoneNumber.replace(/\D/g, "");

	// Ensure the input is exactly 10 digits
	if (cleaned.length === 10) {
		const areaCode = cleaned.slice(0, 3);
		const prefix = cleaned.slice(3, 6);
		const lineNumber = cleaned.slice(6);
		return `(${areaCode}) ${prefix}-${lineNumber}`;
	}

	// Return partially formatted input for less than 10 digits
	if (cleaned.length < 10) {
		if (cleaned.length <= 3) return `(${cleaned}`;
		if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
		return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
	}

	return phoneNumber; // Return as-is for non-standard inputs
};


/**
 * Checks if a phone number is in the format (xxx) xxx-xxxx.
 * @param phoneNumber - The phone number string to validate.
 * @returns True if the phone number is valid, otherwise false.
 */
export const isValidPhone = (phoneNumber: string): boolean => {
	const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
	return phoneRegex.test(phoneNumber);
};



/**
 * Formats a postal code based on the country code.
 * @param postalCode - The postal code string to format.
 * @param countryCode - The country code for formatting.
 * @returns Formatted postal code.
 */
export const formatPostalCode = (postalCode: string, countryCode: string): string => {
	if (!postalCode) return "";

	if (countryCode) {
		const cleanPostalCode = postalCode.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

		if (countryCode.toLowerCase().endsWith("ca")) {
			// Format as Canadian postal code: A1A 1A1
			return cleanPostalCode.toUpperCase().replace(/([A-Z][0-9][A-Z])([0-9][A-Z][0-9])/, "$1 $2");
		}
	}

	// Default: Return the postal code as-is if the country code doesn't match
	return postalCode;
};


